/**
 * BoardAgentChat — the conversational read-surface for a board-first agent.
 *
 * Board-first agents (roster/Troi, allocation, care-planner, access, team-access) answer
 * on the 202-async-turn path: the reply lands in the session message cache via
 * useSessionEventStream, NOT in `streaming.content` — the async path never populates
 * that field (see useStreamingChat). Panels that rendered
 * ONLY `StreamingMessageBubble content={streaming.content}` therefore swallowed
 * every PROSE reply — an explanation, a warning, a refusal, anything without a
 * board-command block. The board folded command replies from the same cache, so
 * only wordless replies "worked" and the swallow stayed invisible until someone
 * asked the agent a plain question ("wie arbeitest du?" → no response).
 *
 * This renders the real transcript (the same MessageList the main chat uses),
 * filtered to conversation: user turns, the agent's prose replies, and the
 * in-flight thinking placeholder. The empty-prose assistant entries that exist
 * only to carry a board-command block would be empty bubbles, so they are
 * dropped here — they still fold onto the board.
 *
 * From the session cut-over on there is no block folding at all: the command
 * block kinds (allocation_commands, access_commands, schedule) have no
 * counterpart in the upstream conversation payload, so foldAllocation /
 * foldAccess are empty and this transcript is the whole conversation.
 *
 * `emptyMessage` shows a hint when there is no conversation yet — for a dedicated
 * chat space like the roster slide-in. Omit it for the board strips: with nothing
 * to show the component renders null (no empty bordered strip), so the strip
 * stays hidden until the agent actually says something in words, preserving the
 * board-first feel.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import type { CitationResponse } from '@/api/types'
import type { SessionDetail } from '@/hooks/useSessions'
import { MessageList } from './MessageList'
import { StreamingMessageBubble } from './StreamingMessageBubble'
import { CitationSheet } from './CitationSheet'
import { usePinToBottom } from '@/hooks/usePinToBottom'
import { useSessionEventStream } from '@/hooks/useSessionEventStream'
import type { useStreamingMessage } from '@/hooks/useStreamingChat'
import { ConversationContext } from './conversationContext'

export function BoardAgentChat({
  id,
  session,
  streaming,
  emptyMessage,
  className,
}: {
  /** DOM id for the scroll region, so a control that toggles this transcript
   *  can point at it with aria-controls (BoardConversationDrawer). */
  id?: string
  session: SessionDetail
  streaming: ReturnType<typeof useStreamingMessage>
  /** Hint shown when there is no conversation yet (dedicated chat spaces only). */
  emptyMessage?: string
  /** Container classes — owns the scroll region so an empty strip renders nothing. */
  className?: string
}) {
  // This component's root IS the scroll region (every call site passes
  // `overflow-y-auto` in className), so the pin belongs here. Board surfaces
  // used to ride MessageList's own scroll; that scroll is gone, and a
  // board-first agent's prose reply has to follow the bottom edge just like
  // the main chat's.
  const { containerRef } = usePinToBottom<HTMLDivElement>()
  const [selectedCitation, setSelectedCitation] = useState<CitationResponse | null>(null)
  useSessionEventStream(session.session.id, useQueryClient())

  const conversational = session.messages.items

  // Once the 202 lands, the pre-allocated placeholder is already in the
  // transcript (as a thinking bubble), so suppress the transient fallback to
  // avoid a duplicate. It then only surfaces a pre-202 send failure.
  const streamingAlreadyRendered =
    !!streaming.assistantMessageId &&
    session.messages.items.some((m) => m.id === streaming.assistantMessageId)
  const showFallback =
    !streamingAlreadyRendered &&
    (streaming.isStreaming || streaming.isRetrying || !!streaming.error)

  const hasList = conversational.length > 0 || !!emptyMessage
  if (!hasList && !showFallback) return null

  return (
    <ConversationContext.Provider value={session.session.id}>
      <div id={id} ref={containerRef} className={className}>
        {/* One wrapper around everything that grows: usePinToBottom observes the
            container's first element child, and the fallback bubble is a sibling
            of the list, so an unwrapped pair would leave the fallback unobserved. */}
        <div>
          {hasList && (
            <MessageList
              messages={conversational}
              isLoading={false}
              isPending={false}
              emptyMessage={emptyMessage ?? ''}
              onCitationClick={setSelectedCitation}
              onRetry={streaming.retry}
              isRetrying={streaming.isRetrying}
            />
          )}
          {showFallback && (
            <div className={hasList ? 'mt-3' : undefined}>
              <StreamingMessageBubble
                content={streaming.content}
                citations={streaming.citations}
                isStreaming={streaming.isStreaming}
                error={streaming.error}
                onRetry={streaming.retry}
                isRetrying={streaming.isRetrying}
              />
            </div>
          )}
        </div>
        <CitationSheet
          citation={selectedCitation}
          open={!!selectedCitation}
          onOpenChange={(o) => !o && setSelectedCitation(null)}
        />
      </div>
    </ConversationContext.Provider>
  )
}
